import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Factory, Pencil, Languages, Search, RotateCcw } from "lucide-react";
import { DuplicatesTab, MatrixTab, DigestTab, GroupsTab, AccessTab } from "./AdminOrg";
import { CommodityGroupTreeEditor } from "../components/srm/CommodityGroupTreeEditor";
import { DICT, LANGS } from "../lib/i18n";

const ROLES = ["admin", "sales_manager", "sales_rep", "customer_service", "read_only"];
const TERRITORIES = ["DACH", "Southern Europe", "LATAM", "Asia"];

const PL_LANGS = ["de", "en", "es", "pt", "it"];
const slugify = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const normOpt = (v) => (typeof v === "string" ? { id: v, labels: { en: v } } : { id: v.id, labels: v.labels || {} });

function PicklistOptionsTab() {
  const { t, reloadPicklists } = useApp();
  const [data, setData] = useState({});
  const [activeKey, setActiveKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newId, setNewId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/config/picklists").then((r) => {
      const d = r.data || {};
      setData(d);
      const keys = Object.keys(d).filter((k) => k !== "segments").sort();
      setActiveKey((prev) => prev || keys[0] || "");
    }).catch(() => {});
  }, []);

  const keys = Object.keys(data).filter((k) => k !== "segments").sort();
  const options = (data[activeKey] || []).map(normOpt);

  const setLabel = (idx, lg, value) => setData((d) => {
    const list = (d[activeKey] || []).map(normOpt);
    list[idx] = { ...list[idx], labels: { ...list[idx].labels, [lg]: value } };
    return { ...d, [activeKey]: list };
  });
  const removeOpt = (idx) => setData((d) => ({ ...d, [activeKey]: (d[activeKey] || []).map(normOpt).filter((_, i) => i !== idx) }));
  const addOpt = () => {
    const id = (newId || slugify(newLabel)).trim();
    if (!id) return;
    setData((d) => {
      const list = (d[activeKey] || []).map(normOpt);
      if (list.some((o) => o.id === id)) { toast.error(t("pl_id_exists")); return d; }
      list.push({ id, labels: { en: newLabel || id } });
      return { ...d, [activeKey]: list };
    });
    setNewLabel(""); setNewId("");
  };
  const save = async () => {
    setSaving(true);
    try { await api.put("/config/picklists", data); reloadPicklists(); toast.success(t("save") + " ✓"); }
    catch (e) { toast.error(errDetail(e)); }
    setSaving(false);
  };

  return (
    <div className="space-y-4 max-w-5xl" data-testid="picklist-options-tab">
      <p className="text-sm text-gray-500">{t("pl_help")}</p>
      <div className="flex items-center gap-3">
        <Select value={activeKey} onValueChange={setActiveKey}>
          <SelectTrigger className="w-72" data-testid="pl-key-select"><SelectValue /></SelectTrigger>
          <SelectContent>{keys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
        </Select>
        <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white ml-auto" onClick={save} disabled={saving} data-testid="pl-save-btn">{t("save")}</Button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[170px_repeat(5,1fr)_40px] gap-2 px-3 py-2 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          <span>ID</span>{PL_LANGS.map((lg) => <span key={lg}>{lg.toUpperCase()}</span>)}<span></span>
        </div>
        {options.map((o, idx) => (
          <div key={o.id} className="grid grid-cols-[170px_repeat(5,1fr)_40px] gap-2 px-3 py-1.5 items-center border-t border-gray-100" data-testid={`pl-opt-${o.id}`}>
            <span className="font-mono text-xs text-gray-500 truncate" title={o.id}>{o.id}</span>
            {PL_LANGS.map((lg) => (
              <Input key={lg} className="h-8 text-sm" value={o.labels[lg] || ""} placeholder={lg === "en" ? o.id : ""}
                data-testid={`pl-label-${o.id}-${lg}`} onChange={(e) => setLabel(idx, lg, e.target.value)} />
            ))}
            <button className="text-gray-300 hover:text-red-500 p-1" onClick={() => removeOpt(idx)} data-testid={`pl-del-${o.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {options.length === 0 && <p className="px-3 py-4 text-sm text-gray-400">–</p>}
      </div>
      <div className="flex items-end gap-2 bg-white border border-gray-200 rounded-lg p-3">
        <div className="space-y-1"><Label className="text-xs">ID</Label>
          <Input className="h-8 w-40 font-mono text-xs" placeholder={slugify(newLabel) || "auto"} value={newId} data-testid="pl-new-id" onChange={(e) => setNewId(e.target.value)} /></div>
        <div className="space-y-1 flex-1"><Label className="text-xs">{t("pl_new_label")}</Label>
          <Input className="h-8" value={newLabel} data-testid="pl-new-label" onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addOpt()} /></div>
        <Button variant="outline" onClick={addOpt} data-testid="pl-add-btn"><Plus className="h-4 w-4 mr-1" />{t("add")}</Button>
      </div>
    </div>
  );
}

function TranslationsTab() {
  const { t, reloadTranslations } = useApp();
  const [lang, setLang] = useState("de");
  const [overrides, setOverrides] = useState({});
  const [query, setQuery] = useState("");
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  const load = () => api.get("/config/translations").then((r) => setOverrides(r.data || {})).catch(() => {});
  useEffect(() => { load(); }, []);

  const langOverrides = overrides[lang] || {};
  const allKeys = Object.keys(DICT.en);
  const q = query.trim().toLowerCase();
  const visibleKeys = q
    ? allKeys.filter((k) => k.toLowerCase().includes(q) || (DICT[lang]?.[k] || "").toLowerCase().includes(q)).slice(0, 60)
    : Object.keys(langOverrides);

  const save = async (key, value) => {
    setSavingKey(key);
    try {
      const r = await api.put("/config/translations", { lang, key, value });
      setOverrides(r.data || {});
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      reloadTranslations();
      toast.success(value ? t("save") + " ✓" : t("deleted"));
    } catch (e) { toast.error(errDetail(e)); }
    setSavingKey(null);
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="translations-tab">
      <p className="text-sm text-gray-500">
        {t("tr_help") || "Override any interface text per language. Empty value restores the built-in translation. Changes apply instantly for all users."}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <Select value={lang} onValueChange={(v) => { setLang(v); setDrafts({}); }}>
          <SelectTrigger className="w-44" data-testid="tr-lang-select"><Languages className="h-4 w-4 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            {LANGS.map((l) => <SelectItem key={l} value={l}>{t(`lang_${l}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search_placeholder")}
            className="pl-9" data-testid="tr-search" />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {visibleKeys.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-gray-400" data-testid="tr-empty">
            {q ? t("no_results") : (t("tr_no_overrides") || "No overrides yet — search a key above to customise a text.")}
          </div>
        )}
        {visibleKeys.map((key) => {
          const base = DICT[lang]?.[key] ?? DICT.en[key] ?? "";
          const current = key in drafts ? drafts[key] : (langOverrides[key] ?? "");
          const isOverridden = langOverrides[key] != null;
          return (
            <div key={key} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`tr-row-${key}`}>
              <div className="sm:w-52 shrink-0">
                <div className="text-xs font-mono text-[#1B2A4A] truncate flex items-center gap-1.5">
                  {key}{isOverridden && <span className="h-1.5 w-1.5 rounded-full bg-[#F5A623]" title="overridden" />}
                </div>
                <div className="text-[11px] text-gray-400 truncate" title={base}>{base}</div>
              </div>
              <Input
                value={current}
                placeholder={base}
                onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                className="flex-1"
                data-testid={`tr-input-${key}`}
              />
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white h-9"
                  disabled={savingKey === key || (key in drafts ? drafts[key] : langOverrides[key] ?? "") === (langOverrides[key] ?? "")}
                  onClick={() => save(key, current)} data-testid={`tr-save-${key}`}>
                  {savingKey === key ? "…" : t("save")}
                </Button>
                {isOverridden && (
                  <Button size="sm" variant="ghost" className="h-9 text-gray-400 hover:text-red-500"
                    onClick={() => save(key, "")} data-testid={`tr-reset-${key}`} title={t("delete")}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EscalationTab() {
  const { t } = useApp();
  const [users, setUsers] = useState([]);
  const [ids, setIds] = useState([]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/config/escalation").then((r) => setIds(r.data.recipient_ids || [])).catch(() => {});
  }, []);
  const toggle = (id) => setIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    try { await api.put("/config/escalation", { recipient_ids: ids }); toast.success(t("escalation_saved")); }
    catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <div className="space-y-4" data-testid="escalation-tab">
      <p className="text-sm text-gray-500">{t("escalation_help")}</p>
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {users.map((u) => (
          <label key={u.id} className="px-4 py-2.5 flex items-center gap-3 text-sm cursor-pointer hover:bg-gray-50" data-testid={`escalation-user-${u.email}`}>
            <input type="checkbox" checked={ids.includes(u.id)} onChange={() => toggle(u.id)} className="h-4 w-4 accent-[#1B2A4A]" />
            <span className="font-medium">{u.name}</span>
            <span className="text-xs text-gray-400">{u.email}</span>
            <Badge variant="outline" className="ml-auto">{u.role}</Badge>
          </label>
        ))}
      </div>
      <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="escalation-save-btn">{t("save")}</Button>
    </div>
  );
}

const SLA_FIELDS = [
  { key: "complaint_ack_hours", min: 1, max: 720, unit: "hours" },
  { key: "artwork_reminder_days", min: 1, max: 90, unit: "days" },
  { key: "contract_renewal_days", min: 1, max: 365, unit: "days" },
];

function SlaTab() {
  const { t } = useApp();
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/config/sla").then((r) => setRules(r.data)).catch(() => {});
  }, []);
  const setField = (key, val) => setRules((p) => ({ ...p, [key]: val }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      SLA_FIELDS.forEach((f) => { payload[f.key] = Number(rules[f.key]); });
      const r = await api.put("/config/sla", payload);
      setRules(r.data);
      toast.success(t("sla_saved"));
    } catch (e) { toast.error(errDetail(e)); }
    setSaving(false);
  };
  if (!rules) return null;
  return (
    <div className="space-y-6 max-w-2xl" data-testid="sla-tab">
      <p className="text-sm text-gray-500">{t("sla_help")}</p>
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {SLA_FIELDS.map((f) => (
          <div key={f.key} className="px-4 py-4 flex items-center gap-4" data-testid={`sla-row-${f.key}`}>
            <div className="flex-1">
              <div className="text-sm font-medium text-[#1B2A4A]">{t(`sla_${f.key}`)}</div>
              <div className="text-xs text-gray-400">{t(`sla_${f.key}_desc`)}</div>
            </div>
            <Input
              type="number"
              min={f.min}
              max={f.max}
              value={rules[f.key] ?? ""}
              onChange={(e) => setField(f.key, e.target.value)}
              className="w-24 text-right"
              data-testid={`sla-input-${f.key}`}
            />
            <span className="text-xs text-gray-500 w-14">{t(f.unit)}</span>
          </div>
        ))}
      </div>
      <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} disabled={saving} data-testid="sla-save-btn">
        {saving ? t("saving") || "…" : t("save")}
      </Button>
    </div>
  );
}


function SsoTab() {
  const { t } = useApp();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const hydrate = (d) => setCfg({
    enabled: !!d.enabled,
    tenant_id: d.tenant_id || "",
    client_id: d.client_id || "",
    has_secret: !!d.has_secret,
    client_secret: "",
    allowed_domains: (d.allowed_domains || []).join(", "),
    callback_url: d.callback_url || "",
  });

  useEffect(() => {
    api.get("/admin/sso").then((r) => hydrate(r.data)).catch((e) => toast.error(errDetail(e)));
  }, []);

  const setF = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!cfg.enabled,
        tenant_id: cfg.tenant_id.trim(),
        client_id: cfg.client_id.trim(),
        allowed_domains: (cfg.allowed_domains || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (cfg.client_secret) payload.client_secret = cfg.client_secret;
      const r = await api.put("/admin/sso", payload);
      hydrate(r.data);
      toast.success(t("sso_saved"));
    } catch (e) { toast.error(errDetail(e)); }
    setSaving(false);
  };

  if (!cfg) return null;
  return (
    <div className="space-y-6 max-w-2xl" data-testid="sso-tab">
      <p className="text-sm text-gray-500">{t("sso_help")}</p>

      <label className="flex items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3 cursor-pointer">
        <input type="checkbox" checked={cfg.enabled} onChange={(e) => setF("enabled", e.target.checked)}
          className="h-4 w-4 accent-[#1B2A4A]" data-testid="sso-enabled-toggle" />
        <span className="text-sm font-medium text-[#1B2A4A]">{t("sso_enabled")}</span>
      </label>

      <div className="space-y-1.5">
        <Label>{t("sso_tenant")}</Label>
        <Input value={cfg.tenant_id} onChange={(e) => setF("tenant_id", e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000" data-testid="sso-tenant-input" />
      </div>
      <div className="space-y-1.5">
        <Label>{t("sso_client_id")}</Label>
        <Input value={cfg.client_id} onChange={(e) => setF("client_id", e.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000" data-testid="sso-client-input" />
      </div>
      <div className="space-y-1.5">
        <Label>{t("sso_secret")}</Label>
        <Input type="password" value={cfg.client_secret} onChange={(e) => setF("client_secret", e.target.value)}
          placeholder={cfg.has_secret ? "•••••••• (gespeichert)" : t("sso_secret_ph")} data-testid="sso-secret-input" />
      </div>
      <div className="space-y-1.5">
        <Label>{t("sso_domains")}</Label>
        <Input value={cfg.allowed_domains} onChange={(e) => setF("allowed_domains", e.target.value)}
          placeholder="ihrefirma.de, tochter.de" data-testid="sso-domains-input" />
        <p className="text-xs text-gray-400">{t("sso_domains_help")}</p>
      </div>
      <div className="space-y-1.5">
        <Label>{t("sso_callback")}</Label>
        <Input readOnly value={cfg.callback_url} className="bg-gray-50 font-mono text-xs" data-testid="sso-callback-url"
          onFocus={(e) => e.target.select()} />
        <p className="text-xs text-gray-400">{t("sso_callback_help")}</p>
      </div>

      <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} disabled={saving} data-testid="sso-save-btn">
        {saving ? t("saving") || "…" : t("save")}
      </Button>
    </div>
  );
}


function SegmentsTab() {
  const { t, reloadPicklists } = useApp();
  const [segments, setSegments] = useState([]);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState({});
  const load = () => api.get("/config/segments").then((r) => setSegments(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const clearRename = (old) => setRenaming((p) => { const c = { ...p }; delete c[old]; return c; });
  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try { const r = await api.post("/config/segments", { name }); setSegments(r.data); setNewName(""); reloadPicklists(); toast.success(t("save") + " ✓"); }
    catch (e) { toast.error(errDetail(e)); }
  };
  const rename = async (old) => {
    const nn = (renaming[old] ?? "").trim();
    if (!nn || nn === old) { clearRename(old); return; }
    try { const r = await api.put("/config/segments", { old, new: nn }); setSegments(r.data); clearRename(old); reloadPicklists(); toast.success(t("segment_renamed")); }
    catch (e) { toast.error(errDetail(e)); clearRename(old); }
  };
  const remove = async (name) => {
    try { const r = await api.delete(`/config/segments/${encodeURIComponent(name)}`); setSegments(r.data); reloadPicklists(); toast.success(t("segment_deleted")); }
    catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="segments-tab">
      <p className="text-sm text-gray-500">{t("segments_help")}</p>
      <div className="flex gap-2">
        <Input data-testid="segment-new-input" placeholder={t("segment_name")} value={newName}
          onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full shrink-0" onClick={add} data-testid="segment-add-btn">
          <Plus className="h-4 w-4 mr-1" />{t("add")}
        </Button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {segments.length === 0 && <p className="p-4 text-sm text-gray-400">–</p>}
        {segments.map((s) => (
          <div key={s} className="px-4 py-2.5 flex items-center gap-3" data-testid={`segment-row-${s}`}>
            <Input className="h-8 flex-1" data-testid={`segment-input-${s}`}
              value={renaming[s] ?? s}
              onChange={(e) => setRenaming((p) => ({ ...p, [s]: e.target.value }))}
              onBlur={() => renaming[s] !== undefined && rename(s)}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />
            <button className="text-gray-300 hover:text-red-500 p-1" onClick={() => remove(s)} data-testid={`segment-delete-${s}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


const EMPTY_PRESS = { name: "", technologies: "", max_colors: 8, max_web_width: 430, substrates: "", finishings: "" };

const toEdit = (p) => ({
  ...p,
  presses: (p.presses || []).map((pr) => ({
    ...pr,
    technologies: (pr.technologies || []).join(", "),
    substrates: (pr.substrates || []).join(", "),
    finishings: (pr.finishings || []).join(", "),
  })),
});

const fromEdit = (p) => ({
  ...p,
  presses: (p.presses || []).map((pr) => ({
    ...pr,
    max_colors: Number(pr.max_colors) || 0,
    max_web_width: Number(pr.max_web_width) || 0,
    technologies: String(pr.technologies || "").split(",").map((s) => s.trim()).filter(Boolean),
    substrates: String(pr.substrates || "").split(",").map((s) => s.trim()).filter(Boolean),
    finishings: String(pr.finishings || "").split(",").map((s) => s.trim()).filter(Boolean),
  })),
});

export default function Admin() {
  const { t, picklists: storePicklists } = useApp();
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState(null);
  const [plants, setPlants] = useState([]);
  const [plantSeg, setPlantSeg] = useState("all");
  const [editingPlant, setEditingPlant] = useState(null);
  const [portalUsers, setPortalUsers] = useState([]);
  const [newPortal, setNewPortal] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [permGroups, setPermGroups] = useState([]);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/plants").then((r) => setPlants(r.data)).catch(() => {});
    api.get("/portal-users").then((r) => setPortalUsers(r.data)).catch(() => {});
    api.get("/accounts?limit=500").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/perm-groups").then((r) => setPermGroups(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const savePlant = async () => {
    const body = fromEdit(editingPlant);
    try {
      if (editingPlant.id) await api.put(`/plants/${editingPlant.id}`, body);
      else await api.post("/plants", body);
      toast.success(t("save") + " ✓");
      setEditingPlant(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const removePlant = async (p) => {
    try { await api.delete(`/plants/${p.id}`); load(); } catch (e) { toast.error(errDetail(e)); }
  };

  const savePortalUser = async () => {
    try {
      await api.post("/portal-users", newPortal);
      toast.success(t("save") + " ✓");
      setNewPortal(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const removePortalUser = async (pu) => {
    try { await api.delete(`/portal-users/${pu.id}`); load(); } catch (e) { toast.error(errDetail(e)); }
  };

  const saveUser = async () => {
    try {
      await api.post("/users", newUser);
      toast.success(t("save") + " ✓");
      setNewUser(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/users/${u.id}`, { role }); toast.success(`${u.name} → ${role}`); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const removeUser = async (u) => {
    try { await api.delete(`/users/${u.id}`); load(); } catch (e) { toast.error(errDetail(e)); }
  };

  const changeGroup = async (u, v) => {
    try {
      await api.put(`/users/${u.id}`, { perm_group_id: v === "builtin" ? "" : v });
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const changeManager = async (u, v) => {
    try { await api.put(`/users/${u.id}`, { manager_id: v === "none" ? "" : v }); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };



  return (
    <div className="space-y-5" data-testid="admin-page">
      <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("admin")}</h1>
      <Tabs defaultValue="picklists">
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="users" data-testid="admin-tab-users">{t("users")}</TabsTrigger>
          <TabsTrigger value="picklists" data-testid="admin-tab-picklists">{t("picklists")}</TabsTrigger>
          <TabsTrigger value="segments" data-testid="admin-tab-segments">{t("segments")}</TabsTrigger>
          <TabsTrigger value="commodity-groups" data-testid="admin-tab-commodity-groups">Warengruppen</TabsTrigger>
          <TabsTrigger value="plants" data-testid="admin-tab-plants">{t("plants")}</TabsTrigger>
          <TabsTrigger value="portal" data-testid="admin-tab-portal">{t("portal_users")}</TabsTrigger>
          <TabsTrigger value="duplicates" data-testid="admin-tab-duplicates">{t("duplicates") || "Duplicates"}</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="admin-tab-matrix">{t("user_location_matrix") || "User–Location matrix"}</TabsTrigger>
          <TabsTrigger value="groups" data-testid="admin-tab-groups">{t("roles_rights")}</TabsTrigger>
          <TabsTrigger value="access" data-testid="admin-tab-access">{t("access_deputy")}</TabsTrigger>
          <TabsTrigger value="digest" data-testid="admin-tab-digest">{t("weekly_digest") || "Weekly digest"}</TabsTrigger>
          <TabsTrigger value="escalation" data-testid="admin-tab-escalation">{t("escalation")}</TabsTrigger>
          <TabsTrigger value="sla" data-testid="admin-tab-sla">{t("sla_rules")}</TabsTrigger>
          <TabsTrigger value="sso" data-testid="admin-tab-sso">{t("sso_tab")}</TabsTrigger>
          <TabsTrigger value="translations" data-testid="admin-tab-translations">{t("translations_tab") || "Translations"}</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="admin-users-migrated">
            <p className="text-sm font-semibold text-[#1B2A4A]">{t("iam_users")}</p>
            <p className="text-sm text-gray-500 mt-1 max-w-xl">{t("admin_moved_users")}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <Link to="/iam/users" data-testid="admin-goto-iam-users"
                className="px-4 h-9 inline-flex items-center rounded-full bg-[#1B2A4A] text-white text-sm font-semibold hover:bg-[#2a3d63]">
                {t("iam_users")}
              </Link>
              <Link to="/iam/roles" data-testid="admin-goto-iam-roles"
                className="px-4 h-9 inline-flex items-center rounded-full border border-gray-200 text-sm font-semibold hover:border-[#E8871E]">
                {t("iam_roles")}
              </Link>
              <Link to="/iam/org" data-testid="admin-goto-iam-org"
                className="px-4 h-9 inline-flex items-center rounded-full border border-gray-200 text-sm font-semibold hover:border-[#E8871E]">
                {t("iam_org")}
              </Link>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="picklists"><PicklistOptionsTab /></TabsContent>

        <TabsContent value="segments"><SegmentsTab /></TabsContent>

        <TabsContent value="commodity-groups"><CommodityGroupTreeEditor /></TabsContent>

        <TabsContent value="plants" className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <Select value={plantSeg} onValueChange={setPlantSeg}>
              <SelectTrigger className="w-56" data-testid="admin-plant-segment-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("segment")}: {t("all")}</SelectItem>
                {(storePicklists.segments || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="add-plant-btn" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full"
              onClick={() => setEditingPlant({ name: "", location: "", region: "DACH", segments: [], presses: [{ ...EMPTY_PRESS }] })}>
              <Plus className="h-4 w-4 mr-1" />{t("add_plant")}
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {plants.filter((p) => plantSeg === "all" || (p.segments || []).includes(plantSeg)).map((p) => (
              <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid="plant-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold flex items-center gap-1.5"><Factory className="h-4 w-4 text-gray-400" />{p.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{p.location} · {p.region}</p>
                    {(p.segments || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {p.segments.map((s) => <Badge key={s} className="bg-amber-50 text-amber-800 hover:bg-inherit font-normal text-[10px]" data-testid="admin-plant-segment-badge">{s}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button className="text-gray-300 hover:text-gray-600 p-1" onClick={() => setEditingPlant(toEdit(p))} data-testid={`edit-plant-${p.name}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button className="text-gray-300 hover:text-red-500 p-1" onClick={() => removePlant(p)} data-testid={`delete-plant-${p.name}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  {(p.presses || []).map((pr, i) => (
                    <div key={i} className="border border-gray-100 rounded-md p-2.5 text-xs">
                      <p className="font-semibold">{pr.name}</p>
                      <p className="text-gray-500 mt-0.5">
                        {(pr.technologies || []).join(", ")} · {pr.max_colors} {t("num_colors")} · {pr.max_web_width} mm
                      </p>
                      <p className="text-gray-400 mt-0.5 truncate">{(pr.substrates || []).join(", ")}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="portal" className="space-y-4">
          <div className="flex justify-end">
            <Button data-testid="add-portal-user-btn" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full"
              onClick={() => setNewPortal({})}>
              <Plus className="h-4 w-4 mr-1" />{t("add_portal_user")}
            </Button>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {portalUsers.length === 0 && <p className="p-4 text-sm text-gray-400">–</p>}
            {portalUsers.map((pu) => (
              <div key={pu.id} className="px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="portal-user-row">
                <div className="min-w-[180px]">
                  <p className="font-medium">{pu.contact_name || pu.email}</p>
                  <p className="text-xs text-gray-400">{pu.email}</p>
                </div>
                <Badge variant="outline">{pu.account_name}</Badge>
                <button className="ml-auto text-gray-300 hover:text-red-500" onClick={() => removePortalUser(pu)} data-testid={`delete-portal-user-${pu.email}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400">Portal-URL: {window.location.origin}/portal</p>
        </TabsContent>

        <TabsContent value="duplicates"><DuplicatesTab /></TabsContent>
        <TabsContent value="matrix"><MatrixTab /></TabsContent>
        <TabsContent value="groups" className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="admin-groups-migrated">
            <p className="text-sm font-semibold text-[#1B2A4A]">{t("iam_roles")}</p>
            <p className="text-sm text-gray-500 mt-1 max-w-xl">{t("admin_moved_roles")}</p>
            <Link to="/iam/roles" data-testid="admin-goto-roles-2"
              className="mt-4 px-4 h-9 inline-flex items-center rounded-full bg-[#1B2A4A] text-white text-sm font-semibold hover:bg-[#2a3d63]">
              {t("iam_roles")}
            </Link>
          </div>
        </TabsContent>

        <TabsContent value="access"><AccessTab /></TabsContent>
        <TabsContent value="digest"><DigestTab /></TabsContent>
        <TabsContent value="escalation"><EscalationTab /></TabsContent>
        <TabsContent value="sla"><SlaTab /></TabsContent>
        <TabsContent value="sso"><SsoTab /></TabsContent>
        <TabsContent value="translations"><TranslationsTab /></TabsContent>
      </Tabs>

      <Dialog open={!!editingPlant} onOpenChange={(o) => !o && setEditingPlant(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPlant?.id ? t("edit") : t("add_plant")}</DialogTitle></DialogHeader>
          {editingPlant && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1"><Label className="text-xs text-gray-500">{t("name")}</Label>
                  <Input data-testid="plant-name-input" value={editingPlant.name} onChange={(e) => setEditingPlant((p) => ({ ...p, name: e.target.value }))} /></div>
                <div className="space-y-1"><Label className="text-xs text-gray-500">{t("location")}</Label>
                  <Input data-testid="plant-location-input" value={editingPlant.location} onChange={(e) => setEditingPlant((p) => ({ ...p, location: e.target.value }))} /></div>
                <div className="space-y-1"><Label className="text-xs text-gray-500">{t("region")}</Label>
                  <Select value={editingPlant.region} onValueChange={(v) => setEditingPlant((p) => ({ ...p, region: v }))}>
                    <SelectTrigger data-testid="plant-region-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{TERRITORIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("segments")}</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 border rounded-md p-2 max-h-40 overflow-y-auto" data-testid="plant-segments-select">
                  {(storePicklists.segments || []).map((s) => {
                    const arr = editingPlant.segments || [];
                    const on = arr.includes(s);
                    return (
                      <label key={s} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`plant-segment-opt-${s}`}>
                        <input type="checkbox" className="h-4 w-4 accent-[#1B2A4A]" checked={on}
                          onChange={() => setEditingPlant((p) => ({ ...p, segments: on ? arr.filter((x) => x !== s) : [...arr, s] }))} />
                        <span className="truncate">{s}</span>
                      </label>
                    );
                  })}
                  {(storePicklists.segments || []).length === 0 && <span className="text-xs text-gray-400">–</span>}
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("presses")}</Label>
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="add-press-btn"
                    onClick={() => setEditingPlant((p) => ({ ...p, presses: [...(p.presses || []), { ...EMPTY_PRESS }] }))}>
                    <Plus className="h-3 w-3 mr-1" />{t("add_press")}
                  </Button>
                </div>
                {(editingPlant.presses || []).map((pr, i) => (
                  <div key={i} className="border border-gray-100 rounded-md p-3 grid grid-cols-2 gap-2 relative">
                    <button className="absolute top-2 right-2 text-gray-300 hover:text-red-500"
                      onClick={() => setEditingPlant((p) => ({ ...p, presses: p.presses.filter((_, j) => j !== i) }))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    {[["name", t("name")], ["technologies", `${t("technology")} (a, b)`],
                      ["max_colors", t("max_colors")], ["max_web_width", t("web_width")],
                      ["substrates", `${t("substrate")} (a, b)`], ["finishings", `${t("finishing")} (a, b)`]].map(([k, lbl]) => (
                      <div key={k} className="space-y-0.5">
                        <Label className="text-[10px] text-gray-400">{lbl}</Label>
                        <Input className="h-8 text-xs" data-testid={`press-${i}-${k}`}
                          type={["max_colors", "max_web_width"].includes(k) ? "number" : "text"}
                          value={pr[k] ?? ""} onChange={(e) => setEditingPlant((p) => ({
                            ...p, presses: p.presses.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)),
                          }))} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={savePlant} data-testid="plant-save-btn">{t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!newPortal} onOpenChange={(o) => !o && setNewPortal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("add_portal_user")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>{t("account")}</Label>
              <Select value={newPortal?.account_id || ""} onValueChange={(v) => setNewPortal((p) => ({ ...p, account_id: v }))}>
                <SelectTrigger data-testid="portal-user-account-select"><SelectValue placeholder="–" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>{t("contact")}</Label>
              <Input data-testid="portal-user-contact-input" value={newPortal?.contact_name || ""} onChange={(e) => setNewPortal((p) => ({ ...p, contact_name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>{t("email")}</Label>
              <Input data-testid="portal-user-email-input" type="email" value={newPortal?.email || ""} onChange={(e) => setNewPortal((p) => ({ ...p, email: e.target.value }))} /></div>
            <div className="space-y-1"><Label>{t("password")}</Label>
              <Input data-testid="portal-user-password-input" type="password" value={newPortal?.password || ""} onChange={(e) => setNewPortal((p) => ({ ...p, password: e.target.value }))} /></div>
            <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={savePortalUser} data-testid="portal-user-save-btn">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newUser} onOpenChange={(o) => !o && setNewUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("add_user")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>{t("name")}</Label>
              <Input data-testid="new-user-name" value={newUser?.name || ""} onChange={(e) => setNewUser((u) => ({ ...u, name: e.target.value }))} /></div>
            <div className="space-y-1"><Label>{t("email")}</Label>
              <Input data-testid="new-user-email" type="email" value={newUser?.email || ""} onChange={(e) => setNewUser((u) => ({ ...u, email: e.target.value }))} /></div>
            <div className="space-y-1"><Label>{t("password")}</Label>
              <Input data-testid="new-user-password" type="password" value={newUser?.password || ""} onChange={(e) => setNewUser((u) => ({ ...u, password: e.target.value }))} /></div>
            <div className="space-y-1"><Label>{t("role")}</Label>
              <Select value={newUser?.role} onValueChange={(v) => setNewUser((u) => ({ ...u, role: v }))}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1"><Label>{t("territory")}</Label>
              <Select value={newUser?.territory} onValueChange={(v) => setNewUser((u) => ({ ...u, territory: v }))}>
                <SelectTrigger data-testid="new-user-territory"><SelectValue /></SelectTrigger>
                <SelectContent>{TERRITORIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select></div>
            <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={saveUser} data-testid="new-user-save-btn">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
